
import { getRequestLogs } from '@/core/services/admin-service'
import { LogRow, RequestLogGroup, RequestLogsResponse } from '@/types/api/admin/log-row'
import { useVirtualList } from '@vueuse/core'
import { computed, defineComponent, reactive, toRefs } from 'vue'
import JsonEditorVue from 'json-editor-vue'
import 'vanilla-jsoneditor/themes/jse-theme-dark.css'

const GROUP_SUMMARY_HEIGHT = 132

export default defineComponent({
  components: { JsonEditorVue },
  setup() {
    const state = reactive({
      apiResponse: null as null | RequestLogsResponse,
      requestId: undefined as string | undefined,
      fromDate: new Date().toISOString().split('T')[0],
      contract: '',
      errorsOnly: false,
      expandedRows: new Set<string>(),
      expandedGroupKey: null as null | string
    })

    const groups = computed(() => state.apiResponse?.groups ?? [])
    const totalEventCount = computed(() => groups.value.reduce((sum, group) => sum + group.eventCount, 0))
    const expandedGroup = computed(() => groups.value.find(group => getGroupIdentity(group) === state.expandedGroupKey) ?? null)

    function getGroupIdentity(group: RequestLogGroup) {
      return `${group.correlationId ?? 'no-correlation-id'}-${group.loggedOnDate}`
    }

    function getGroupKey(group: RequestLogGroup, index: number) {
      return `${getGroupIdentity(group)}-${index}`
    }

    function getRowKey(group: RequestLogGroup, row: LogRow, rowIndex: number) {
      return `${getGroupIdentity(group)}-${row.loggedOnDate}-${rowIndex}-${row.message ?? ''}`
    }

    function isRowExpanded(group: RequestLogGroup, row: LogRow, rowIndex: number) {
      return state.expandedRows.has(getRowKey(group, row, rowIndex))
    }

    function isExpandedGroup(group: RequestLogGroup) {
      return state.expandedGroupKey === getGroupIdentity(group)
    }

    const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(groups, {
      itemHeight: GROUP_SUMMARY_HEIGHT,
      overscan: 4
    })

    async function getLogs() {
      state.apiResponse = await getRequestLogs(
        state.requestId,
        state.fromDate,
        state.contract || undefined,
        state.errorsOnly
      )
      state.expandedRows.clear()
      state.expandedGroupKey = null
      scrollTo(0)
    }

    function toggleGroup(group: RequestLogGroup) {
      const groupKey = getGroupIdentity(group)
      if(state.expandedGroupKey === groupKey) {
        state.expandedGroupKey = null
        return
      }

      state.expandedGroupKey = groupKey
      state.expandedRows.clear()
    }

    function toggleRowExpand(group: RequestLogGroup, row: LogRow, rowIndex: number) {
      const rowId = getRowKey(group, row, rowIndex)
      if (state.expandedRows.has(rowId)) {
        state.expandedRows.delete(rowId)
      } else {
        state.expandedRows.add(rowId)
      }
    }

    function getSummaryMessage(group: RequestLogGroup) {
      const latestRow = group.rows[group.rows.length - 1]
      return latestRow?.message ?? 'No message'
    }

    function formatDate(dateStr: string) {
      const date = new Date(dateStr)
      return new Intl.DateTimeFormat(
        'en-GB',
        {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      ).format(date)
    }

    function formatTime(dateStr: string) {
      const date = new Date(dateStr)
      return date.toLocaleTimeString(
        'en-GB',
        {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      )
    }

    function formatContracts(contracts: string[]) {
      if(contracts.length === 1) {
        return `Contract: ${contracts[0]}`
      }

      return `${contracts.length} contracts`
    }

    function getDataSummary(data: unknown) {
      if(Array.isArray(data)) {
        return `[${data.length} items]`
      }

      if(data && typeof data === 'object') {
        return '{...}'
      }

      return 'No data'
    }

    return {
      containerProps,
      expandedGroup,
      formatContracts,
      getDataSummary,
      getGroupKey,
      getLogs,
      getRowKey,
      getSummaryMessage,
      isExpandedGroup,
      isRowExpanded,
      list,
      formatDate,
      formatTime,
      toggleGroup,
      toggleRowExpand,
      totalEventCount,
      wrapperProps,
      ...toRefs(state)
    }
  }
})
